/* eslint-disable react/require-default-props */
import React from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import RiskLevelTag from "#/modules/common/RiskLevelTag";
import StatusTag from "#/modules/common/StatusTag";

type PolicyListItemProps = {
  active?: boolean;
  name: string;
  enabled: boolean;
  applied: number;
  threats: number;
  checks: number;
  riskLevel: number;
  onClick: () => void;
  onMouseDown?: () => void;
};

const PolicyCard = styled.button<{ active?: boolean }>`
  width: 100%;
  text-align: left;
  border-radius: 4px;
  padding: 12px;
  border: 0;
  background: var(--x-purple-500, #494358);
  color: var(--x-gray-200);
  cursor: pointer;
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  box-shadow: ${(props) => (props.active ? "inset 0 0 0 1px var(--x-gray-200, #f6f6f7)" : "none")};
`;

const PolicyTitleRow = styled.div`
  width: 100%;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--x-purple-400, #494358);
`;

const PolicyTitle = styled.div`
  color: var(--x-gray-200, #f6f6f7);
  font-size: 14px;
  font-family: Inter, sans-serif;
  font-weight: 700;
  line-height: 21px;
  word-break: break-word;
  flex: 1;
  min-width: 0;
`;

const PolicyMetaRow = styled.div`
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
`;

const PolicyCardStats = styled.div`
  width: 100%;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const StatTile = styled.div`
  flex: 1 1 0;
  background: var(--x-bg-400, #3b3649);
  border-radius: 4px;
  padding: 3px 0;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const StatValue = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: var(--Score-Color-Normal-(100-89), #34d7e6);
  line-height: 21px;
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: var(--x-gray-400, #cfd0d3);
  line-height: 21px;
`;

const PolicyListItem = ({
  active,
  name,
  enabled,
  applied,
  threats,
  checks,
  riskLevel,
  onClick,
  onMouseDown,
}: PolicyListItemProps): JSX.Element => {
  const { t } = useTranslation("policySpec");
  return (
    <PolicyCard active={active} onClick={onClick} onMouseDown={onMouseDown}>
      <PolicyTitleRow>
        <PolicyTitle>{name}</PolicyTitle>
        <RiskLevelTag level={riskLevel} />
      </PolicyTitleRow>
      <PolicyMetaRow>
        <StatusTag enabled={enabled} />
        <div style={{ color: "var(--x-gray-500)" }}>
          {`${t("leftPanel.appliedTo")} `}
          <span style={{ color: "var(--x-gray-200)" }}>
            {applied}
          </span>
          {` ${applied === 1 ? t("leftPanel.applicationSingular") : t("leftPanel.applicationPlural")}`}
        </div>
      </PolicyMetaRow>
      <PolicyCardStats>
        <StatTile>
          <StatValue>{threats}</StatValue>
          <StatLabel>{t("leftPanel.violationPolicies")}</StatLabel>
        </StatTile>
        <StatTile>
          <StatValue>{checks}</StatValue>
          <StatLabel>{t("leftPanel.totalChecks")}</StatLabel>
        </StatTile>
      </PolicyCardStats>
    </PolicyCard>
  );
};

export default PolicyListItem;
